import React, { useEffect, useState, useRef } from "react";
import { FaSearch } from "react-icons/fa";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";

const SearchBar = ({
  value,
  defaultValue = "",
  onChange,
  onSearch,
  placeholder = "Search reports by keyword…",
  debounce = 0,
  showButton = false,
  buttonText = "Search",
  className = "",
  enableSuggestions = false,
  isAdmin = false,
  onSuggestionSelect = null,
}) => {
  const [input, setInput] = useState(value ?? defaultValue);
  const [isTyping, setIsTyping] = useState(false);
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const navigate = useNavigate();
  const wrapperRef = useRef(null);

  useEffect(() => {
    if (value !== undefined) setInput(value);
  }, [value]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
        setShowSuggestions(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const onChangeRef = useRef(onChange);
  useEffect(() => {
    onChangeRef.current = onChange;
  }, [onChange]);

  useEffect(() => {
    if (input !== value) {
      setIsTyping(true);
    }

    const handler = setTimeout(
      () => {
        if (onChangeRef.current) onChangeRef.current(input);
        setIsTyping(false);

        if (enableSuggestions && input && input.length >= 2) {
          fetchSuggestions(input);
        } else {
          setSuggestions([]);
          setShowSuggestions(false);
        }
      },
      debounce > 0 ? debounce : 300,
    );

    return () => clearTimeout(handler);
  }, [input, debounce, value, enableSuggestions]);

  const fetchSuggestions = async (q) => {
    try {
      const res = await axios.get(
        `${process.env.REACT_APP_API_BASE_URL || "/api"}/reports/suggestions?q=${encodeURIComponent(q)}`,
      );
      setSuggestions(res.data);
      setShowSuggestions(res.data.length > 0);
    } catch (err) {
      console.error("Error fetching suggestions:", err);
    }
  };

  const handleChange = (e) => setInput(e.target.value);

  const handleSubmit = () => {
    setSuggestions([]);
    setShowSuggestions(false);
    if (onSearch) onSearch(input);
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      handleSubmit();
    }
  };

  const { endUser, saveSearchReport } = useAuth() || {};

  const handleSuggestionClick = (suggestion) => {
    setInput(suggestion.title);
    setSuggestions([]);
    setShowSuggestions(false);
    if (onSuggestionSelect) {
      onSuggestionSelect(suggestion);
    } else {
      if (isAdmin) {
        navigate(`/admin/reports/edit/${suggestion.id}`);
      } else {
        if (endUser && saveSearchReport) {
          saveSearchReport("", suggestion);
        }
        navigate(`/reports/${suggestion.slug || suggestion.id}`, {
          state: { report: suggestion },
        });
      }
    }
  };

  return (
    <div
      className={`search-container w-100 position-relative ${className}`}
      ref={wrapperRef}
    >
      <div className="search-input-wrapper border d-flex align-items-center bg-white rounded-pill ps-3 pe-1 py-md-1 shadow-sm">
        {isTyping ? (
          <div
            className="spinner-border spinner-border-sm text-secondary me-2"
            role="status"
          >
            <span className="visually-hidden">Loading...</span>
          </div>
        ) : (
          <FaSearch className="text-muted me-2 ms-1" size={18} />
        )}
        <input
          type="text"
          className="form-control border-0 shadow-none px-0 py-0"
          placeholder={placeholder}
          value={input}
          onChange={handleChange}
          onKeyDown={handleKeyDown}
          onFocus={() => {
            if (suggestions.length > 0) setShowSuggestions(true);
          }}
          style={{ fontSize: "0.85rem", border: "0px", outline: "none" }}
          aria-label={placeholder}
        />
        {showButton && (
          <button
            className="searchBtn btn btn-search-bg btn-rw-search px-4 py-md-1 ms-2 text-white"
            onClick={handleSubmit}
            aria-label={buttonText}
          >
            {buttonText}
          </button>
        )}
      </div>

      {showSuggestions && (
        <div
          className="suggestions-dropdown position-absolute w-100 bg-white border rounded shadow-lg mt-1"
          style={{
            zIndex: 1000,
            maxHeight: "300px",
            overflowY: "auto",
            top: "100%",
            left: 0,
          }}
        >
          {suggestions.map((s, index) => (
            <div
              key={index}
              className="suggestion-item p-2 hover-bg-light cursor-pointer border-bottom last-border-0"
              onClick={() => handleSuggestionClick(s)}
              style={{ fontSize: "0.9rem" }}
            >
              <div className="text-dark fw-medium text-start">
                {s.title ? s.title.replace(/\s*-\s*$/, "") : ""}
              </div>
            </div>
          ))}
          {!isAdmin && (
            <div
              className="p-2 text-center border-top text-primary fw-bold cursor-pointer"
              style={{ fontSize: "0.85rem" }}
              onClick={handleSubmit}
            >
              Explore All Reports
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default SearchBar;
